import { useMemo, useState, useEffect, useRef } from 'react';
import '../../accounting/accounting.css';
import '../../modules/SettingsDetailPage.css'; // full-page `.sd-*` detail frame + the standard field grid
import './positionDetail.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { runWithAuditReason } from '../../../api/client';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useNavActions } from '../../../hooks/useNavActions';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { guardedExit } from '../../../hooks/useUnsavedGuard';
import { usePositions, useSavePosition, useDeletePosition, errMessage, type Position } from './systemMasters';

interface Props { onHome?: () => void; }

/** Settings ▸ System Management ▸ Position — CRUD over dbo.Position (Name + Sort + Division-based flag).
 *  List as legacy: New · Delete · Refresh · Export to · Search, one inline-edit pencil per row. New and
 *  a click on a row open a full-page detail (a row opens read-only with an Edit button). */
export function PositionPage({ onHome }: Props) {
  const notify = useNotify();
  // Module Access Control: hide (never grey) the actions the signed-in role does not hold.
  const perm = useNavActions('sm-position');
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = usePositions();
  const loadMs = useFetchMs(isFetching);
  const save = useSavePosition();
  const del = useDeletePosition();

  const [search, setSearch] = useState('');

  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // The rows in the order the grid shows them (sorted / filtered) — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<{ id: string }[]>([]);
  const [editing, setEditing] = useState<Position | null | undefined>(undefined);
  const [name, setName] = useState('');
  const [sort, setSort] = useState('');
  const [divBased, setDivBased] = useState(true);   // "Is Division Based" — ticked on a new position, as legacy
  const [err, setErr] = useState('');
  // Inline row-edit (the row pencil) — Position + Sort in place. IsDivisionBased is edited on the
  // detail page; an inline save keeps it as stored.
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [dName, setDName] = useState('');
  const [dSort, setDSort] = useState('');
  const [savingInline, setSavingInline] = useState(false);

  // View → Edit (owner request 2026-10-12): a click on a row opens the record READ-ONLY with an Edit
  // button; Edit turns the boxes and Save on. New always opens editable.
  const [mode, setMode] = useState<'view' | 'edit'>('edit');
  const viewOnly = mode === 'view' && !!editing?.id;
  const openNew = () => {
    // Sort is pre-filled with the next number (highest + 1), as legacy does; it can still be changed.
    const nextSort = rows.reduce((m, r) => Math.max(m, r.sort ?? 0), 0) + 1;
    setMode('edit'); setEditing(null); setName(''); setSort(String(nextSort)); setDivBased(true); setErr('');
  };
  const openView = (r: Position) => { openEdit(r); setMode('view'); };
  const openEdit = (r: Position) => { setMode(perm.write ? 'edit' : 'view'); setEditing(r); setName(r.positionName ?? ''); setSort(r.sort != null ? String(r.sort) : ''); setDivBased(r.isDivisionBased); setErr(''); };
  const close = () => setEditing(undefined);
  // Cancel / breadcrumb / record arrows: ask before dropping unsaved edits (standing rule 15).
  const leave = () => { void guardedExit(close); };

  const startInline = (r: Position) => { setInlineId(r.id); setDName(r.positionName ?? ''); setDSort(r.sort != null ? String(r.sort) : ''); };
  const cancelInline = () => { setInlineId(null); setDName(''); setDSort(''); };
  const saveInline = async (r: Position) => {
    if (!dName.trim()) { notify.warning("'Position' must not be empty"); return; }
    // Reason-for-change gate (standing rule 11) — only when ReasonRequired is ON.
    const reason = await gateReason('Position', 'edit');
    if (reason === null) return;
    setSavingInline(true);
    try {
      await runWithAuditReason(reason, () =>
        save.mutateAsync({ id: r.id, positionName: dName.trim(), sort: dSort.trim() === '' ? null : Number(dSort), isDivisionBased: r.isDivisionBased }));
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      cancelInline();
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSavingInline(false); }
  };

  const submit = async (after: 'stay' | 'close' | 'new' = 'close') => {
    if (viewOnly) return;   // nothing to save while only viewing
    if (!name.trim()) { setErr("'Position' must not be empty"); return; }
    setErr('');
    const sortNum = sort.trim() === '' ? null : Number(sort);
    // Reason-for-change gate (standing rule 11) — only on an EDIT; Create is exempt.
    let reason = '';
    if (editing?.id) {
      const r = await gateReason('Position', 'edit');
      if (r === null) return;
      reason = r;
    }
    const wasNew = !editing?.id;
    let savedId: string | undefined;
    await runSave({
      save: () => runWithAuditReason(reason, async () => {
        const res: unknown = await save.mutateAsync({ id: editing?.id, positionName: name.trim(), sort: sortNum, isDivisionBased: divBased });
        savedId = editing?.id ?? (typeof res === 'string' ? res : (res as { data?: { data?: { id?: string } } } | null)?.data?.data?.id);
      }),
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (after === 'close') { close(); return; }
        if (after === 'new') { openNew(); return; }
        // 'stay' — a brand-new record becomes the one being edited; if its id did not come back, return to the list.
        if (wasNew) { if (savedId) setEditing({ id: savedId, positionName: name.trim(), sort: sortNum, isDivisionBased: divBased }); else close(); }
        else if (editing) setEditing({ ...editing, positionName: name.trim(), sort: sortNum, isDivisionBased: divBased });
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one position to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} position${selectedIds.size === 1 ? '' : 's'}?` })) return;
    const reason = await gateReason('Position', 'delete');
    if (reason === null) return;
    try { await runWithAuditReason(reason, async () => { for (const id of selectedIds) await del.mutateAsync(String(id)); }); setSelectedIds(new Set()); await alert({ title: 'Success', message: 'Deleted.', tone: 'success' }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  // Detail shortcuts through the shared hotkey layer (standing rule 14).
  useHotkeys(editing === undefined ? [] : viewOnly ? [
    ...(perm.write ? [
      { combo: 'alt+e', label: 'Edit', group: 'Position', order: 1, run: () => setMode('edit') },
      { combo: 'f2', label: 'Edit', group: 'Position', order: 2, run: () => setMode('edit') },
    ] : []),
    { combo: 'esc', label: 'Close', group: 'Position', order: 3, allowInInput: false, run: close },
  ] : [
    { combo: 'mod+s', label: 'Save', group: 'Position', order: 1, run: () => void submit('stay') },
    ...(perm.create ? [{ combo: 'mod+shift+s', label: 'Save & New', group: 'Position', order: 2, run: () => void submit('new') }] : []),
    { combo: 'mod+enter', label: 'Save & Close', group: 'Position', order: 3, run: () => void submit('close') },
    { combo: 'esc', label: 'Cancel', group: 'Position', order: 4, allowInInput: false, run: leave },
  ]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => (r.positionName ?? '').toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  const columns: AppColumn<Position>[] = [
    // The row pencil starts an in-place EDIT — hidden for a role without Write.
    ...(perm.write ? [{
      field: 'inlineedit', header: '', noExport: true, sortable: false, filterable: false, width: 44, align: 'center' as const,
      render: (r: Position) =>
        inlineId === r.id ? (
          <span className="pts-rowedit" onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={savingInline} onClick={() => void saveInline(r)}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={savingInline} onClick={cancelInline}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Inline edit" onClick={(e) => { e.stopPropagation(); startInline(r); }}><i className="ti ti-pencil" /></button>
        ),
    }] : []),
    {
      field: 'positionName', header: 'Position', minWidth: 260, text: (r) => r.positionName ?? '',
      compare: (a, b) => (a.positionName ?? '').localeCompare(b.positionName ?? '', undefined, { sensitivity: 'base' }),
      render: (r) => inlineId === r.id ? (
        <input className="pts-input" autoFocus value={dName} disabled={savingInline} onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(r); if (e.key === 'Escape') cancelInline(); }} />
      ) : (<span>{r.positionName ?? ''}</span>),
    },
    {
      field: 'sort', header: 'Sort', width: 110, align: 'right', type: 'number', sortable: false, value: (r) => r.sort ?? null,
      render: (r) => inlineId === r.id ? (
        <input className="pts-input" type="number" value={dSort} disabled={savingInline} onClick={(e) => e.stopPropagation()} onChange={(e) => setDSort(e.target.value)} />
      ) : (<span>{r.sort ?? ''}</span>),
    },
    {
      // Legacy list column. Display only here — it is changed on the detail page.
      field: 'isDivisionBased', header: 'IsDivisionBased', width: 150, align: 'center', sortable: false, text: (r) => (r.isDivisionBased ? 'Yes' : 'No'),
      render: (r) => <span className={`cellcheck${r.isDivisionBased ? ' on' : ''}`} role="img" aria-label={r.isDivisionBased ? 'Yes' : 'No'} />,
    },
  ];

  // ── Full-page detail (New / row click) — replaces the list while open (standing rule 12; owner
  //    request 2026-10-12: legacy opens the detail view, not a popup). A row opens READ-ONLY with
  //    an Edit button; New opens editable. Fields on the shared standard detail grid. ──
  if (editing !== undefined) {
    const recordName = editing ? (editing.positionName ?? '').trim() || 'Position' : 'New Position';
    const curIdx = editing ? rows.findIndex((r) => r.id === editing.id) : -1;
    const prevRow = curIdx > 0 ? rows[curIdx - 1] : null;
    const nextRow = curIdx >= 0 && curIdx < rows.length - 1 ? rows[curIdx + 1] : null;
    const go = (r: Position) => void guardedExit(() => (viewOnly ? openView : openEdit)(r));
    const actions = (bottom?: boolean) => viewOnly ? (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        {perm.write && <button className="sd-abtn primary" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>}
        <button className="sd-abtn cancel" onClick={close} title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
      </div>
    ) : (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => void submit('stay')} disabled={save.isPending} title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> {save.isPending ? 'Saving…' : 'Save'}</button>
        <button className="sd-abtn" onClick={() => void submit('close')} disabled={save.isPending} title={`Save and Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save and Close</button>
        {perm.create && <button className="sd-abtn" onClick={() => void submit('new')} disabled={save.isPending} title={`Save and New (${formatCombo('mod+shift+s')})`}><span className="tb-i">📄</span> Save and New</button>}
        <span className="sd-abtn-sep" aria-hidden="true" />
        <button className="sd-abtn cancel" onClick={leave} disabled={save.isPending} title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
      </div>
    );
    return (
      <div className="bl-page mlv-host">
        {/* List/Detail switch (as City / Payment Terms Setup): "List View" goes back to the list, asking first when
            there are unsaved changes. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(); }} />
        </div>
        <div className="bl-grid-area">
          <div className="sd-wrap">
            <div className="sd-head">
              <div>
                <h2>Position – {recordName}</h2>
                <div className="sd-bc">
                  <a onClick={() => void guardedExit(() => onHome?.())}>Home</a><i className="ti ti-chevron-right" />
                  <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
                  <a onClick={leave}>Position</a><i className="ti ti-chevron-right" /><span>{recordName}</span>
                </div>
              </div>
              <div className="sd-navarrows">
                <button className="sd-na" title="Previous record" disabled={!prevRow || save.isPending} onClick={() => prevRow && go(prevRow)}>‹</button>
                <button className="sd-na next" title="Next record" disabled={!nextRow || save.isPending} onClick={() => nextRow && go(nextRow)}>›</button>
              </div>
            </div>

            {actions()}

            <div className="sd-body">
              <div className="sd-grid sd-grid--single sd-grid--side sd-grid--square">
                <div className="sd-row">
                  <label className="sd-label" htmlFor="pos-name">Position<span className="req">*</span></label>
                  <input id="pos-name" className={`sd-input${viewOnly ? ' readonly' : ''}${err ? ' required-empty' : ''}`} readOnly={viewOnly} autoFocus={!viewOnly}
                    value={name} onChange={(e) => { setName(e.target.value); if (err) setErr(''); }} />
                  {err && <div className="sd-field-error">{err}</div>}
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="pos-sort">Sort</label>
                  <input id="pos-sort" className={`sd-input${viewOnly ? ' readonly' : ''}`} readOnly={viewOnly} type="number" placeholder="(auto)"
                    value={sort} onChange={(e) => setSort(e.target.value)} />
                </div>
                {/* Legacy shows this tick under the boxes with its caption to the right of it. */}
                <div className="sd-row">
                  <span className="sd-label" aria-hidden="true" />
                  <label className="pos-check">
                    <input type="checkbox" checked={divBased} disabled={viewOnly} onChange={(e) => setDivBased(e.target.checked)} />
                    Is Division Based
                  </label>
                </div>
              </div>
            </div>

            {actions(true)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Position</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        {/* Breadcrumb on top, then the List/Detail switch beside the FormTitle (as City / Payment Terms Setup, owner
            request 2026-10-15). "Detail View" opens the ticked record — or the first one — read-only. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <ViewToggle view="list" detailDisabled={rows.length === 0}
            onChange={(v) => {
              if (v !== 'detail') return;
              const id = selectedIds.size === 1 ? String([...selectedIds][0]) : (shownRef.current[0] ?? rows[0])?.id;
              const r = rows.find((x) => x.id === id); if (r) openView(r);
            }} />
          <FormTitle pageId="sm-position" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Position" />
        </div>
      </div>
      <div className="acc-body acc-search-tight">
        {/* Action bar as legacy: New · Delete · Refresh · Export to · Search (no Edit — a row opens by a
            click, or is edited in place with its pencil). */}
        {!gridMax && (
        <AppToolbar
          onNew={perm.create ? openNew : undefined}
          onDelete={perm.delete ? () => void removeSelected() : undefined} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          // Export to on the action bar (owner request 2026-10-09); Search sits on the search row above the grid (2026-10-14).
          right={
            <>
              <ExportMenu triggerClassName="ca-btn" filename="Position" headers={['Position', 'Sort', 'IsDivisionBased']} rows={visible.map((r) => [r.positionName ?? '', r.sort != null ? String(r.sort) : '', r.isDivisionBased ? 'Yes' : 'No'])} />
            </>
          }
        />
        )}
        {/* ONE white grid card — grid + its single footer only. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="Position" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card">
          <AppDataGrid<Position> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            onVisibleRowsChange={(r) => { shownRef.current = r; }} selectedRowId={inlineId}
            onRowClick={(r) => { if (!inlineId) openView(r); }}
            initialSort={{ key: 'positionName', dir: 'asc' }} loading={isFetching} emptyText="No positions found." hideHeaderTools />
        </div>
      </div>

    </div>
  );
}
